import { trackEvent } from '@event-tracking';
import type {
  GeneoIotProjectFile,
  GeneoIotTelemetryEvent,
} from '../GeneoIotFrame';

/**
 * Ingest boundary for Geneo IoT usage telemetry.
 *
 * The frame emits pseudonymous behaviour — enum-valued props, no identity and
 * no content. Resource context is attached here; student, school and session
 * identity is attached server-side by backend-r2 from the authenticated
 * session, and never travels in the request body.
 *
 * See geneo-iot `ai-context/usage-tracking.md` for the normative event
 * contract, and §6.3 for the three requirements this path has to meet:
 * non-blocking, failure-tolerant, and idempotent.
 */

/** The nine event names the frame is specified to emit. */
const KNOWN_EVENTS = new Set([
  'session_start',
  'session_heartbeat',
  'session_end',
  'workspace_snapshot',
  'sim_run',
  'device_connect_result',
  'flash_result',
  'project_saved',
  'error_shown',
]);

/**
 * Namespace so these cannot collide with event types from any other surface,
 * and so `event_type LIKE 'gil.%'` selects the whole GIL catalogue.
 */
const PREFIX = 'gil.';

export interface IotTelemetryContext {
  resourceId?: string;
  contentId?: string;
  /**
   * The project the frame last pushed, attached to `project_saved`.
   *
   * A getter rather than a value because the frame pushes the project over
   * `geneo:project` the moment the student saves, while the `project_saved`
   * telemetry arrives in a batch up to 30s later: read at call time this is
   * the project as saved, captured at render time it would be whatever the
   * host was holding when the frame mounted.
   */
  getProject?: () => GeneoIotProjectFile | undefined;
}

/**
 * Ceiling for an attached project.
 *
 * A project file is unbounded - workspace XML plus generated code - while the
 * ingest route rejects a request body over 256 KB. 128 KB keeps a single
 * project_saved comfortably shippable on its own (batches are now capped by
 * bytes, so it travels alone if it has to). Over that, the project is left out
 * and its size recorded, because a request that can never succeed would cost
 * the whole batch a 413.
 */
const PROJECT_MAX_BYTES = 128 * 1024;

/**
 * The saved project, as payload.
 *
 * Note this is the one place the GIL telemetry path carries content rather
 * than pseudonymous counters. That is deliberate - a saved project is the
 * artefact the work produced, and there is no reconstructing it from event
 * counts - but it means `gil.project_saved` payloads hold student work, and
 * retention for them is whatever `event_raw`'s partition drop schedule says.
 */
function projectPayload(
  project: GeneoIotProjectFile | undefined,
): Record<string, unknown> {
  if (!project) {
    // The telemetry arrived without the frame ever pushing a project. Recorded
    // rather than ignored: it means the two channels disagree, and silence
    // would make that indistinguishable from "no save happened".
    return { projectAttached: false, projectOmittedReason: 'not-received' };
  }

  let bytes: number;
  try {
    bytes = JSON.stringify(project).length;
  } catch {
    return { projectAttached: false, projectOmittedReason: 'unserialisable' };
  }

  if (bytes > PROJECT_MAX_BYTES) {
    return {
      projectAttached: false,
      projectOmittedReason: 'too-large',
      projectBytes: bytes,
      // Enough to still tell the projects apart and see what was lost.
      projectMeta: project.meta,
      projectBoard: project.board,
    };
  }

  return { projectAttached: true, projectBytes: bytes, project };
}

export function ingestIotTelemetry(
  events: GeneoIotTelemetryEvent[],
  ctx: IotTelemetryContext,
): void {
  for (const event of events) {
    // Unknown names are dropped rather than forwarded: the contract is
    // normative and fixed, so an unrecognised name means a frame newer than
    // this host, and guessing at its shape would pollute the catalogue.
    if (!KNOWN_EVENTS.has(event.name)) continue;

    trackEvent(
      `${PREFIX}${event.name}`,
      {
        ...event.props,
        resourceId: ctx.resourceId,
        contentId: ctx.contentId,
        appVersion: event.appVersion,
        // Only project_saved carries the artefact. Attaching it to every
        // event would multiply one project across a whole batch of
        // heartbeats and snapshots.
        ...(event.name === 'project_saved'
          ? projectPayload(ctx.getProject?.())
          : {}),
      },
      {
        // The frame's own session id — distinct from the LMS session the
        // server stamps. Keeps a single editor session joinable across all
        // nine event types, and survives the frame being remounted.
        iotSessionId: event.sessionId,
        // Batch counter. Gaps mean a batch was lost between frame and host,
        // which is the only way to see that loss at all.
        iotSeq: event.seq,
        resourceId: ctx.resourceId,
      },
      // Resolved against the host anchor, not the receipt time: the frame
      // buffers for up to 30s, so receipt time would collapse a whole batch
      // onto one instant and destroy the durations these events measure.
      event.occurredAt,
    );
  }
}
